import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';
import BrandLockup from '../components/BrandLockup';
import { accountVerificationApi } from '../api/accountVerificationApi';
import { useProfile } from '../context/ProfileContext';

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const { verification, isVerificationHydrated, refreshVerification, hasMembership, logout } = useProfile();
  const [email, setEmail] = useState(verification?.email ?? '');
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (verification?.email) setEmail(verification.email);
    if (isVerificationHydrated && verification?.isVerified) {
      navigate(verification.isProfileConfirmed && hasMembership ? '/setup' : '/onboarding', { replace: true });
    }
  }, [hasMembership, isVerificationHydrated, navigate, verification]);

  const checkStatus = async () => {
    setIsChecking(true);
    setMessage(null);
    setIsError(false);
    try {
      const status = await refreshVerification();
      if (status?.isVerified) {
        navigate(status.isProfileConfirmed && hasMembership ? '/setup' : '/onboarding', { replace: true });
      } else {
        setMessage('Your email is still waiting for confirmation. Open the link in your inbox, then check again.');
      }
    } catch {
      setIsError(true);
      setMessage('We could not check your status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const resend = async () => {
    setIsResending(true);
    setMessage(null);
    setIsError(false);
    try {
      const status = await accountVerificationApi.resend(email.trim());
      setEmail(status.email ?? email);
      setMessage(`A new confirmation email was sent to ${status.email}.`);
    } catch {
      setIsError(true);
      setMessage('Enter a valid email address and try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="onboarding-page">
      <div className="onboarding-page__scene" aria-hidden="true" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/onboarding-bg.jpg)` }} />
      <div className="onboarding-page__shade" aria-hidden="true" />
      <section className="onboarding-card onboarding-card--narrow">
        <div className="onboarding-card__brand"><BrandLockup tone="onLight" compact /></div>
        <div className="onboarding-card__header">
          <h1>Check your email</h1>
          <p>We sent a confirmation link to your email address. Confirm it before continuing your CaddieSight setup.</p>
        </div>

        <div className="onboarding-field verification-email">
          <label htmlFor="verification-email">Email address</label>
          <InputText
            id="verification-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            readOnly={Boolean(verification?.email)}
            autoComplete="email"
          />
        </div>

        {message ? (
          <div className={`onboarding-note${isError ? ' onboarding-note--error' : ''}`} role="status">
            {message}
          </div>
        ) : null}

        <div className="onboarding-actions verification-actions">
          <Button label="Check status" icon="pi pi-refresh" loading={isChecking} onClick={checkStatus} />
          <Button label="Resend email" icon="pi pi-envelope" outlined loading={isResending} disabled={!email.trim()} onClick={resend} />
        </div>
        <button type="button" className="onboarding-link" onClick={logout}>Sign out</button>
      </section>
    </div>
  );
};

export default VerifyEmailPage;